// components
import { Tooltip } from '@mui/material';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Icon, IconButton } from '@sdb/planning-ui';
import SlicedTable from './components/SlicedTable';

// hooks
import { useEffect, useState } from 'react';
import { useSelector } from 'hooks';
import { useTranslation } from 'react-i18next';

// types
import type {
  OpenRequestsProps,
  TotalActionsEntry
} from './OpenRequests.types';

// styles
import { StyledRowStackAlignItemsSmallGap } from 'components/shared/Stack/Stack.styles';
import { StyledTitleTypography } from './TasksList.styles';
import { StyledPaper } from 'components/shared/Paper/StyledPaper.styles';
import {
  StyledBreakdownNameCell,
  StyledBreakdownTableRow,
  StyledDateTypography,
  StyledNameCellContent,
  StyledExpandToggleSpacer,
  StyledOpenRequestsStack,
  StyledOpenRequestsTableBox,
  StyledOpenRequestsTableCell,
  StyledOpenRequestsTableContainer
} from './OpenRequests.styles';

// utils
import axios from 'axios';
import classNames from 'classnames';
import { format } from 'date-fns';
import dayjs from 'dayjs';
import { noop } from 'lodash';
import { getPlanunitCommaIds } from 'components/utils';
import { getData } from 'RestAPIs/quality-monitor';
import {
  buildRows,
  countTotalActions,
  formatShortDate,
  getRowKey,
  OPEN_REQUEST_STATUS_ICONS
} from './OpenRequests.utils';
import weekOfYear from 'dayjs/plugin/weekOfYear';

dayjs.extend(weekOfYear);

export default function OpenRequests({
  setIsLoading,
  selectedPlanUnits,
  setAmountOfOpenDuties
}: OpenRequestsProps) {
  const { t } = useTranslation('common');
  const [data, setData] = useState<TotalActionsEntry[]>([]);
  const [loadingPlanUnitID, setLoadingPlanUnitID] = useState(0);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const planUnits = useSelector((state) => state.planUnits);
  const startOfThisWeek = dayjs().startOf('week').toDate();
  const endOfThisWeek = dayjs().endOf('week').add(1, 'day').toDate();
  const startOfThisWorkweek = dayjs().startOf('week').toDate();
  const endOfThisWorkweek = dayjs().endOf('week').subtract(1, 'day').toDate();
  const [isWorkweek, setIsWorkweek] = useState(false);
  const [dateRange, setDateRange] = useState({
    startOfWeek: startOfThisWeek,
    endOfWeek: endOfThisWeek
  });

  const formattedStartDate = formatShortDate(dateRange?.startOfWeek);

  const formattedEndDate = formatShortDate(dateRange?.endOfWeek, 1);

  const weekNumber = dayjs(dateRange?.startOfWeek).week();

  const isThisWeek =
    dayjs(startOfThisWeek).toISOString() ===
    dayjs(dateRange.startOfWeek).toISOString();

  useEffect(() => {
    const { CancelToken } = axios;
    const source = CancelToken.source();

    if (selectedPlanUnits.length) {
      const costCenterIds = getPlanunitCommaIds(selectedPlanUnits);
      if (!costCenterIds) {
        setData([]);
        return noop;
      }

      const params = {
        costCenterIds,
        startDate: format(dateRange?.startOfWeek, 'yyyy-MM-dd'),
        endDate: format(dateRange?.endOfWeek, 'yyyy-MM-dd')
      };

      setIsLoading(true);
      getData({ params, cancelToken: source.token }).then(
        ({ data }) => {
          setData(
            data.map((entry) => ({
              ...entry,
              days: entry.days.map((day) => ({
                ...day,
                date: new Date(day.date)
              }))
            }))
          );
          setIsLoading(false);
        },
        (err) => {
          if (!(err && err.message === 'unmount')) {
            setIsLoading(false);
          }
        }
      );
    } else {
      setData([]);
    }

    return () => source.cancel('unmount');
  }, [
    selectedPlanUnits,
    setIsLoading,
    dateRange.startOfWeek,
    dateRange.endOfWeek
  ]);

  useEffect(() => {
    const totalActions = countTotalActions(data);
    setAmountOfOpenDuties(totalActions);
  }, [data]);

  const openRoster = (selectedPlanUnitId, date?, isWeek4View?) => {
    date = date || new Date();
    date.setHours(0, 0, 0, 0);

    window.dojoTopic.publish(
      'setRosterTempPlanunits',
      planUnits.filter(({ id }) => selectedPlanUnitId === id),
      date,
      isWeek4View
    );
    setLoadingPlanUnitID(selectedPlanUnitId);
    // TODO: remove loaders after ROSTER migration is done
  };

  if (!data.length) {
    return <></>;
  }

  const toggleExpanded = (costCenterId: number) => {
    setExpandedIds((previous) => {
      const next = new Set(previous);
      if (next.has(costCenterId)) {
        next.delete(costCenterId);
      } else {
        next.add(costCenterId);
      }
      return next;
    });
  };

  const rows = buildRows(data, expandedIds);

  const handleWeekOrWorkweekToggle = () => {
    if (isWorkweek) {
      setIsWorkweek(false);
      setDateRange({
        startOfWeek: startOfThisWeek,
        endOfWeek: endOfThisWeek
      });
    } else {
      setIsWorkweek(true);
      setDateRange({
        startOfWeek: startOfThisWorkweek,
        endOfWeek: endOfThisWorkweek
      });
    }
  };

  const handleNextPeriod = (next = false, thisWeek = false) => {
    if (thisWeek) {
      setDateRange({
        startOfWeek: isWorkweek ? startOfThisWorkweek : startOfThisWeek,
        endOfWeek: isWorkweek ? endOfThisWorkweek : endOfThisWeek
      });
    } else if (next) {
      setDateRange({
        startOfWeek: dayjs(dateRange.startOfWeek).add(1, 'week').toDate(),
        endOfWeek: dayjs(dateRange.endOfWeek).add(1, 'week').toDate()
      });
    } else {
      setDateRange({
        startOfWeek: dayjs(dateRange.startOfWeek).subtract(1, 'week').toDate(),
        endOfWeek: dayjs(dateRange.endOfWeek).subtract(1, 'week').toDate()
      });
    }
  };

  return (
    <StyledPaper className="open-requests">
      <StyledOpenRequestsStack>
        <StyledRowStackAlignItemsSmallGap>
          <Icon type="calendar-star" fontSize="medium" />
          <StyledTitleTypography variant="h6">
            {t('openRequests')}
          </StyledTitleTypography>
        </StyledRowStackAlignItemsSmallGap>

        <StyledRowStackAlignItemsSmallGap>
          {!isThisWeek && (
            <Tooltip title={t('thisWeek')} arrow>
              <IconButton
                type="calendar-star"
                onClick={() => handleNextPeriod(false, true)}
              />
            </Tooltip>
          )}
          <Tooltip
            title={
              isWorkweek
                ? t('qualityMonitor.setToWeek')
                : t('qualityMonitor.setToWorkweek')
            }
            arrow
          >
            <IconButton
              type="calendar-clock"
              onClick={handleWeekOrWorkweekToggle}
            />
          </Tooltip>
          <Tooltip title={t('previousWeek')} arrow>
            <IconButton
              type="chevron-left"
              onClick={() => handleNextPeriod(false)}
            />
          </Tooltip>
          <StyledDateTypography variant="body1">
            {t('qualityMonitor.fromToDate', {
              startDate: formattedStartDate,
              endDate: formattedEndDate
            })}
            <Chip label={`Week ${weekNumber}`} size="small" />
          </StyledDateTypography>
          <Tooltip title={t('nextWeek')} arrow>
            <IconButton
              type="chevron-right"
              onClick={() => handleNextPeriod(true)}
            />
          </Tooltip>
        </StyledRowStackAlignItemsSmallGap>
      </StyledOpenRequestsStack>

      <StyledOpenRequestsTableBox>
        <StyledOpenRequestsTableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {/* leeg: kop weg, cel blijft voor de rijhoogte van de header */}
                <StyledOpenRequestsTableCell
                  component="th"
                  scope="row"
                  aria-label={t('planUnits')}
                />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) =>
                row.type === 'costCenter' ? (
                  <TableRow
                    key={getRowKey(row)}
                    className={classNames({ 'is-striped': row.striped })}
                  >
                    <TableCell component="th" scope="row">
                      <StyledNameCellContent>
                        {row.hasBreakdown ? (
                          <IconButton
                            type={
                              expandedIds.has(row.costCenterId)
                                ? 'chevron-down'
                                : 'chevron-right'
                            }
                            size="small"
                            aria-expanded={expandedIds.has(row.costCenterId)}
                            aria-label={
                              expandedIds.has(row.costCenterId)
                                ? t('qualityMonitor.openRequestStatus.collapse')
                                : t('qualityMonitor.openRequestStatus.expand')
                            }
                            onClick={() => toggleExpanded(row.costCenterId)}
                          />
                        ) : (
                          <StyledExpandToggleSpacer aria-hidden>
                            <IconButton type="chevron-right" size="small" />
                          </StyledExpandToggleSpacer>
                        )}
                        <button
                          type="button"
                          className="plain-btn"
                          onClick={() =>
                            openRoster(row.costCenterId, null, true)
                          }
                        >
                          {row.costCenterName}
                        </button>
                        {loadingPlanUnitID === row.costCenterId && (
                          <i className="icon-spinner icon-spin" />
                        )}
                      </StyledNameCellContent>
                    </TableCell>
                  </TableRow>
                ) : (
                  <StyledBreakdownTableRow
                    key={getRowKey(row)}
                    className={classNames('is-breakdown', {
                      'is-striped': row.striped
                    })}
                  >
                    <StyledBreakdownNameCell component="th" scope="row">
                      <StyledNameCellContent>
                        <StyledExpandToggleSpacer aria-hidden>
                          <IconButton type="chevron-right" size="small" />
                        </StyledExpandToggleSpacer>
                        <Icon
                          type={OPEN_REQUEST_STATUS_ICONS[row.category]}
                          fontSize="small"
                        />
                        {t(`qualityMonitor.openRequestStatus.${row.category}`)}
                      </StyledNameCellContent>
                    </StyledBreakdownNameCell>
                  </StyledBreakdownTableRow>
                )
              )}
            </TableBody>
          </Table>
        </StyledOpenRequestsTableContainer>
        <SlicedTable
          headerDays={data[0].days}
          rows={rows}
          start={0}
          end={1}
          openRoster={openRoster}
        />
        <SlicedTable
          headerDays={data[0].days}
          rows={rows}
          start={1}
          end={8}
          openRoster={openRoster}
        />
      </StyledOpenRequestsTableBox>
    </StyledPaper>
  );
}
